import Flickity from 'flickity';

import debounce from '../util/debounce';

const selectors = {
  slider: '[data-slider]',
  slide: '[data-slide]',
  slideValue: 'data-slide',
  prevArrow: '[data-prev-arrow]',
  nextArrow: '[data-next-arrow]',
  slideshowSlideImg: '.slide-image-img',
  heroContent: '.hero__content',
  heroContentWrapper: '.hero__content__wrapper',
  dataSliderAnimate: 'data-slider-animate',
  dataAspectRatio: 'data-aspectratio',
  dataDots: 'data-dots',
  dataAutoplay: 'data-autoplay',
  dataAutoplaySpeed: 'data-speed',
  dataColor: 'data-color',
  dataInfinite: 'data-infinite',
  dataSetHeight: 'data-set-height',
  dataWatchCss: 'data-watch-css',
  dataAdaptiveHeight: 'data-adaptive-height',
  dataCellAlign: 'data-cell-align',
  dataButtons: 'data-buttons',
  dataDraggable: 'data-draggable',
  dataPercentPosition: 'data-percent-position',
  dataSlideIndex: 'data-slide-index',
  dataSlidesLargeDesktop: 'data-slides-large-desktop',
  dataSlidesDesktop: 'data-slides-desktop',
  dataSlidesTabletDesktop: 'data-slides-tablet',
  dataSlidesMobileDesktop: 'data-slides-mobile',
  dataSliderStartIndex: 'data-slider-start-index',
  dataGroupCells: 'data-group-cells',
};

const classes = {
  classIsSelected: 'is-selected',
  textDark: 'text-dark',
  textLight: 'text-light',
  transparentWrapper: 'transparent__wrapper',
  heroContentTransparent: 'hero__content--transparent',
  classSliderInitialized: 'js-slider--initialized',
  classSliderArrowsHidden: 'flickity-button-hide',
  classAosAnimate: 'aos-animate',
  classAosAnimated: 'aos-animated',
};

const sections = {};

class Slider {
  constructor(container) {
    this.container = container;
    this.slideshow = this.container.querySelector(selectors.slider);
    if (!this.slideshow) return;
    this.slideshowSlides = this.slideshow.querySelectorAll(selectors.slide);
    this.sliderPrev = this.container.querySelector(selectors.prevArrow);
    this.sliderNext = this.container.querySelector(selectors.nextArrow);
    this.currentSlideColor = this.slideshowSlides[0].getAttribute(selectors.dataColor);
    this.showDots = this.slideshow.getAttribute(selectors.dataDots) === 'true';
    this.autoPlay = this.slideshow.getAttribute(selectors.dataAutoplay) === 'true';
    this.autoPlaySpeed = this.slideshow.getAttribute(selectors.dataAutoplaySpeed);
    this.infinite = this.slideshow.getAttribute(selectors.dataInfinite) !== 'false';
    this.setMinHeightFlag = this.slideshow.getAttribute(selectors.dataSetHeight) === 'true';
    this.watchCss = this.slideshow.getAttribute(selectors.dataWatchCss) === 'true';
    this.adaptiveHeight = this.slideshow.getAttribute(selectors.dataAdaptiveHeight) !== 'false';
    this.buttons = this.slideshow.getAttribute(selectors.dataButtons) !== 'false';
    this.cellAlignLeft = this.slideshow.getAttribute(selectors.dataCellAlign) === 'left';
    this.cellAlignRight = this.slideshow.getAttribute(selectors.dataCellAlign) === 'right';
    this.draggable = this.slideshow.getAttribute(selectors.dataDraggable) !== 'false';
    this.percentPosition = this.slideshow.getAttribute(selectors.dataPercentPosition) !== 'false';
    this.multipleSlides = this.slideshow.hasAttribute(selectors.dataSlidesLargeDesktop);
    this.sliderStartIndex = this.slideshow.hasAttribute(selectors.dataSliderStartIndex);
    this.groupCells = this.slideshow.getAttribute(selectors.dataGroupCells) === 'true';
    this.sliderAnimate = this.slideshow.getAttribute(selectors.dataSliderAnimate) === 'true';
    this.resizeEvent = debounce(() => this.resizeEvents(), 100);
    this.flkty = null;

    this.init();
  }

  init() {
    this.setMinHeight();

    this.flkty = new Flickity(this.slideshow, {
      initialIndex: this.sliderStartIndex ? parseInt(this.slideshow.getAttribute(selectors.dataSliderStartIndex)) : 0,
      autoPlay: this.autoPlay && this.autoPlaySpeed ? parseInt(this.autoPlaySpeed) : false,
      prevNextButtons: this.buttons,
      contain: true,
      pageDots: this.showDots,
      adaptiveHeight: this.adaptiveHeight,
      wrapAround: this.infinite,
      percentPosition: this.percentPosition,
      watchCSS: this.watchCss,
      cellAlign: this.cellAlignLeft ? 'left' : this.cellAlignRight ? 'right' : 'center',
      groupCells: this.groupCells,
      draggable: this.draggable ? '>1' : false,
      on: {
        ready: () => {
          if (this.sliderAnimate && !this.autoPlay) {
            const currentSlide = this.slideshow.querySelector(`.${classes.classIsSelected}`);
            currentSlide.classList.add(classes.classAosAnimated);
          }

          this.slideActions();

          if (this.slideshow.classList.contains(classes.classIsSelected)) {
            this.slideshow.classList.remove(classes.classIsSelected);
          }

          this.showArrows();
        },
      },
    });

    if (this.sliderPrev) {
      this.sliderPrev.addEventListener('click', (e) => {
        e.preventDefault();

        this.flkty.previous(true);
      });
    }

    if (this.sliderNext) {
      this.sliderNext.addEventListener('click', (e) => {
        e.preventDefault();

        this.flkty.next(true);
      });
    }

    this.flkty.on('change', () => this.slideActions());

    if (this.sliderAnimate) {
      this.flkty.on('settle', () => this.sliderSettle());
    }

    if (this.setMinHeightFlag || this.multipleSlides) {
      window.addEventListener('resize', this.resizeEvent);
    }
  }

  sliderSettle() {
    const animatedItems = this.slideshow.querySelectorAll(`.${classes.classIsSelected}:not(.${classes.classAosAnimated}) .${classes.classAosAnimated}`);
    if (animatedItems.length) {
      animatedItems.forEach((animatedItem) => {
        animatedItem.classList.add(classes.classAosAnimate);
        animatedItem.closest(`.${classes.classIsSelected}`).classList.add(classes.classAosAnimated);
      });
    }
  }

  resizeEvents() {
    this.setMinHeight();

    if (this.multipleSlides) {
      this.showArrows();
      this.flkty.resize();
      if (!this.slideshow.classList.contains(classes.classSliderInitialized)) {
        this.flkty.select(0);
      }
    }
  }

  slideActions() {
    const currentSlide = this.slideshow.querySelector(`.${classes.classIsSelected}`);
    this.currentSlideColor = currentSlide.getAttribute(selectors.dataColor);

    if (this.currentSlideColor) {
      this.slideshow.classList.remove(classes.textLight, classes.textDark);
      this.slideshow.classList.add(this.currentSlideColor);
    }

    this.container.classList.remove(classes.transparentWrapper);
    const heroContentWrapper = currentSlide.querySelector(selectors.heroContentWrapper);
    if (heroContentWrapper && heroContentWrapper.classList.contains(classes.heroContentTransparent)) {
      this.container.classList.add(classes.transparentWrapper);
    }

    this.setMinHeight();

    if (this.sliderAnimate) {
      const animatedItems = this.slideshow.querySelectorAll(`.${classes.classIsSelected}:not(.${classes.classAosAnimated}) .${classes.classAosAnimate}`);
      if (animatedItems.length) {
        animatedItems.forEach((animatedItem) => {
          animatedItem.classList.remove(classes.classAosAnimate);
          animatedItem.classList.add(classes.classAosAnimated);
        });
      }
    }
  }

  setMinHeight() {
    if (!this.setMinHeightFlag) return;
    this.slideshowSlides.forEach((element) => {
      const slideImageImg = element.querySelector(selectors.slideshowSlideImg);
      let slideAspectRatio = '';
      if (slideImageImg && slideImageImg.hasAttribute(selectors.dataAspectRatio)) {
        slideAspectRatio = slideImageImg.getAttribute(selectors.dataAspectRatio);
      }

      let slideTextContentHeight = 0;
      let getMargin = 0;
      const slideTextContent = element.querySelector(selectors.heroContent);
      if (slideTextContent) {
        const getMarginTop = parseInt(window.getComputedStyle(slideTextContent).marginTop);
        const getMarginBottom = parseInt(window.getComputedStyle(slideTextContent).marginBottom);
        getMargin = getMarginTop + getMarginBottom;
        slideTextContentHeight = slideTextContent.offsetHeight + getMargin;
      }

      const slideWidth = parseInt(getComputedStyle(element, null).width.replace('px', ''));
      let slideHeight = parseInt(slideWidth / slideAspectRatio) || 0;
      const isCurrentSlide = element.classList.contains(classes.classIsSelected);

      if (slideTextContentHeight > slideHeight) {
        slideHeight = slideTextContentHeight;
      }

      const minHeightValue = `calc(${slideHeight}px + var(--header-padding)`;
      element.style.setProperty('min-height', minHeightValue);
      const heroContentWrapper = element.querySelector(selectors.heroContentWrapper);
      if (heroContentWrapper) {
        heroContentWrapper.style.setProperty('min-height', minHeightValue);
      }
      if (isCurrentSlide) {
        this.slideshow.parentElement.style.setProperty('min-height', minHeightValue);
      }
    });
  }

  showArrows() {
    if (!this.multipleSlides) return;
    const slidesNumberCustom = parseInt(this.slideshow.getAttribute(selectors.dataSlidesLargeDesktop));
    const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    const desktopSlides = this.slideshow.hasAttribute(selectors.dataSlidesDesktop) ? parseInt(this.slideshow.getAttribute(selectors.dataSlidesDesktop)) : 3;
    const tabletSlides = this.slideshow.hasAttribute(selectors.dataSlidesTabletDesktop) ? parseInt(this.slideshow.getAttribute(selectors.dataSlidesTabletDesktop)) : 2;
    const mobileSlides = this.slideshow.hasAttribute(selectors.dataSlidesMobileDesktop) ? parseInt(this.slideshow.getAttribute(selectors.dataSlidesMobileDesktop)) : 1;
    const largeDesktopCheck = windowWidth > 1339 && this.slideshowSlides.length > slidesNumberCustom;
    const desktopCheck = windowWidth <= 1339 && windowWidth > 1023 && this.slideshowSlides.length > desktopSlides;
    const tabletCheck = windowWidth <= 1023 && windowWidth > 750 && this.slideshowSlides.length > tabletSlides;
    const mobileCheck = windowWidth <= 750 && this.slideshowSlides.length > mobileSlides;
    const flag = Boolean(largeDesktopCheck || desktopCheck || tabletCheck || mobileCheck);
    this.slideshow.classList.toggle(classes.classSliderArrowsHidden, !flag);
    this.slideshow.classList.toggle(classes.classSliderInitialized, flag);
  }

  onUnload() {
    if (this.setMinHeightFlag || this.multipleSlides) {
      window.removeEventListener('resize', this.resizeEvent);
    }

    if (!this.slideshow) return;
    this.flkty.destroy();
  }

  onBlockSelect(evt) {
    if (!this.slideshow) return;
    // Ignore the cloned version
    const slide = this.slideshow.querySelector(`[${selectors.slideValue}="${evt.detail.blockId}"]`);
    if (!slide) return;
    let slideIndex = parseInt(slide.getAttribute(selectors.dataSlideIndex));

    if (this.multipleSlides && !this.slideshow.classList.contains(classes.classSliderInitialized)) {
      slideIndex = 0;
    }

    this.slideshow.classList.add(classes.classIsSelected);

    // Go to selected slide, pause autoplay
    this.flkty.select(slideIndex);
    this.flkty.stopPlayer();
  }

  onBlockDeselect() {
    if (!this.slideshow) return;
    this.slideshow.classList.remove(classes.classIsSelected);

    if (!this.autoPlay) return;
    this.flkty.playPlayer();
  }
}

const slider = {
  onLoad() {
    sections[this.id] = new Slider(this.container);
  },
  onUnload(e) {
    sections[this.id].onUnload(e);
  },
  onBlockSelect(e) {
    sections[this.id].onBlockSelect(e);
  },
  onBlockDeselect(e) {
    sections[this.id].onBlockDeselect(e);
  },
};

export {slider, Slider};
